import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../api/endpoints';
import { LoadingPill } from '../components/LoadingPill';
import { useIsAdmin } from '../features/auth/internal';
import { CoverageJurisdictionTable } from '../features/coverage/CoverageJurisdictionTable';
import { ParcelCoverageMap } from '../features/coverage/ParcelCoverageMap';
import { PermitCoverageMap } from '../features/coverage/PermitCoverageMap';
import { PopulationCoverageMap } from '../features/coverage/PopulationCoverageMap';
import { JurisdictionsTable } from '../features/permit-detail/JurisdictionsTable';

/** Save `content` as a file in the browser. */
function download(filename: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Every jurisdiction we hold permits for. Prospects get the kept columns;
 *  admins the full internal table (#171). */
function JurisdictionSection({ isAdmin }: { isAdmin: boolean }) {
  const prospect = useQuery({
    queryKey: ['coverage-jurisdictions'],
    queryFn: api.coverageJurisdictions,
    enabled: !isAdmin,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const full = useQuery({
    queryKey: ['jurisdictions'],
    queryFn: api.jurisdictions,
    enabled: isAdmin,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" component="h2">
        Jurisdictions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every city, town and county we hold permits for: how many permits, how far back, how
        current, and what kinds of work. Hover a column for its definition; sort by any column.
      </Typography>
      {isAdmin ? (
        full.data ? (
          <JurisdictionsTable rows={full.data.jurisdictions} />
        ) : (
          <LoadingPill label="Loading jurisdictions…" />
        )
      ) : prospect.data ? (
        <CoverageJurisdictionTable
          rows={prospect.data.jurisdictions}
          onDownload={(csv) =>
            download(`mollusk-jurisdictions-${prospect.data.generated_at.slice(0, 10)}.csv`, csv)
          }
        />
      ) : (
        <LoadingPill label="Loading jurisdictions…" />
      )}
    </Box>
  );
}

function CoveragePage() {
  const isAdmin = useIsAdmin();
  const [tab, setTab] = useState(0);
  const planning = useQuery({
    queryKey: ['coverage-planning'],
    queryFn: api.coveragePlanning,
    enabled: isAdmin && tab === 2,
  });
  const contractors = useQuery({
    queryKey: ['coverage-contractors'],
    queryFn: api.coverageContractors,
    enabled: isAdmin && tab === 3,
  });
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Coverage
        </Typography>
        {isAdmin ? (
          // Built live from the database on click (#173); can take a minute.
          <Button variant="outlined" href="/api/admin/coverage-export">
            Export coverage (.xlsx)
          </Button>
        ) : null}
      </Box>
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2 }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="Permits" />
          <Tab label="Parcels" />
          {/* Internal QA tabs: admins only (#171). */}
          {isAdmin ? <Tab label="Planning & zoning" /> : null}
          {isAdmin ? <Tab label="Contractors" /> : null}
        </Tabs>
        {/* Render only the active map — Mapbox can't initialise inside a
            display:none container. Query data + geometry are cached, so
            re-mounting on tab switch is cheap. */}
        {tab === 0 && <PermitCoverageMap />}
        {tab === 1 && <ParcelCoverageMap />}
        {tab === 2 && planning.data && (
          <PopulationCoverageMap
            states={planning.data.states}
            jurisdictions={planning.data.jurisdictions}
            recordNoun="planning and zoning cases"
            methodology="Share of the US population living in a state, city, or county-balance jurisdiction for which we hold admitted planning or zoning case records. Overlapping city and county populations are not double-counted."
          />
        )}
        {tab === 3 && contractors.data && (
          <PopulationCoverageMap
            states={contractors.data.states}
            jurisdictions={contractors.data.jurisdictions}
            recordNoun="contractor credentials"
            methodology="Share of the US population living in a jurisdiction covered by an applicable public contractor credential roster. State licensing authorities cover the state; local rosters cover their place. This measures geographic availability, not completeness of every trade class."
          />
        )}
      </Paper>
      {tab === 0 ? <JurisdictionSection isAdmin={isAdmin} /> : null}
    </Box>
  );
}

export const Route = createFileRoute('/coverage')({ component: CoveragePage });
